import React, { useRef, useState } from 'react';
import { MoreVertical, Eye, Download, RefreshCw, Pencil, Trash2, History, FileText } from 'lucide-react';
import { useRest, restFetch, RCard } from './restui';

const API = process.env.REACT_APP_BACKEND_URL;

function Menu({ items, testid }) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const btnRef = useRef();
  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setUp(window.innerHeight - r.bottom < items.length * 38 + 20);
    }
    setOpen(!open);
  };
  return (
    <span className="relative">
      <button ref={btnRef} onClick={toggle} data-testid={testid}
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors">
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <>
          <span className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <span className={`absolute right-0 z-20 w-52 rounded-xl border border-gray-200 bg-white py-1 shadow-xl block ${up ? 'bottom-8' : 'top-8'}`}>
            {items.map(([label, Icon, fn, danger]) => (
              <button key={label} onClick={() => { setOpen(false); fn(); }}
                data-testid={`${testid}-${label.toLowerCase().replace(/\s/g, '-')}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[12.5px] transition-colors ${danger ? 'text-red-500 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </span>
        </>
      )}
    </span>
  );
}

export function DataVault({ isEs, isDemo }) {
  const { data: d, reload } = useRest('/vault');
  const [preview, setPreview] = useState(null);
  const [versionsOf, setVersionsOf] = useState(null);
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const replaceRef = useRef();
  const replaceTarget = useRef(null);

  if (!d) return null;
  const allKinds = [...new Set((d.categories || []).flatMap((c) => c.files.map((f) => f.kind)))];
  const categories = (d.categories || []).map((c) => ({
    ...c,
    files: c.files.filter((f) => (!q || f.filename.toLowerCase().includes(q.toLowerCase())) && (!kind || f.kind === kind)),
  })).filter((c) => c.files.length);

  const act = async (path, opts, okMsg) => {
    const r = await restFetch(path, opts);
    if (r.ok) { setMsg(okMsg); reload(); } else { const j = await r.json().catch(() => ({})); setMsg(`✕ ${j.detail || 'error'}`); }
    setTimeout(() => setMsg(''), 4000);
  };
  const doPreview = async (f) => {
    const r = await restFetch(`/vault/${f.id}/preview`);
    if (r.ok) setPreview(await r.json());
  };
  const download = (f) => { window.open(`${API}/api/rest/vault/${f.id}/download`, '_blank'); };
  const rename = (f) => {
    const name = window.prompt(isEs ? 'Nuevo nombre:' : 'New name:', f.filename);
    if (name && name !== f.filename) act(`/vault/${f.id}/rename`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ filename: name }) }, '✓ renamed');
  };
  const del = (f, isVersion) => {
    if (!window.confirm(isEs ? `¿Eliminar "${f.filename}"${isVersion ? ' (versión)' : ' y todas sus versiones'}? Los reportes se recalculan solos.` : `Delete "${f.filename}"${isVersion ? ' (version)' : ' and all versions'}? Reports recalculate automatically.`)) return;
    act(`/vault/${f.id}`, { method: 'DELETE' }, isEs ? '✓ eliminado · analytics actualizados' : '✓ deleted · analytics updated');
  };
  const replace = (f) => { replaceTarget.current = f; replaceRef.current?.click(); };
  const onReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    const f = replaceTarget.current;
    if (!file || !f) return;
    const fd = new FormData();
    fd.append('file', file);
    await act(`/vault/${f.id}/replace`, { method: 'POST', body: fd },
      isEs ? '✓ reemplazado · KPIs, Intelligence y Health Score recalculados' : '✓ replaced · KPIs, Intelligence and Health Score reprocessed');
    e.target.value = '';
  };
  const restore = (v) => act(`/vault/${v.id}/restore`, { method: 'POST' }, isEs ? '✓ versión restaurada · analytics recalculados' : '✓ version restored · analytics reprocessed');

  return (
    <div data-testid="data-vault">
      <input ref={replaceRef} type="file" accept=".csv" className="hidden" onChange={onReplaceFile} data-testid="vault-replace-input" />
      {msg && <p className="mt-4 text-[13px] font-semibold text-emerald-600" data-testid="vault-msg">{msg}</p>}

      {(d.categories || []).length > 0 && (
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="vault-filters">
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="vault-search-input"
            placeholder={isEs ? 'Buscar archivo…' : 'Search files…'}
            className="h-9 w-56 rounded-full border border-gray-200 bg-white px-4 text-[12.5px] focus:outline-none focus:border-[var(--rest-accent)]" />
          <button onClick={() => setKind('')} data-testid="vault-filter-all"
            className={`rounded-full px-3 py-1.5 text-[11.5px] font-bold uppercase transition-colors ${!kind ? 'bg-[var(--rest-accent)] text-white' : 'bg-white border border-gray-200 text-gray-500 hover:text-gray-900'}`}>
            {isEs ? 'Todos' : 'All'}
          </button>
          {allKinds.map((k) => (
            <button key={k} onClick={() => setKind(kind === k ? '' : k)} data-testid={`vault-filter-${k}`}
              className={`rounded-full px-3 py-1.5 text-[11.5px] font-bold uppercase transition-colors ${kind === k ? 'bg-[var(--rest-accent)] text-white' : 'bg-white border border-gray-200 text-gray-500 hover:text-gray-900'}`}>
              {k}
            </button>
          ))}
        </div>
      )}

      {categories.map((cat) => (
        <RCard key={cat.name} className="mt-5" testid={`vault-cat-${cat.name.toLowerCase().replace(/\s/g, '-')}`}>
          <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
            <FileText className="w-3.5 h-3.5 text-[var(--rest-accent)]" />
            <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">{cat.name}</p>
            <span className="text-[11px] text-gray-300">· {cat.files.length} {isEs ? 'archivos' : 'files'}</span>
          </div>
          {cat.files.map((f) => (
            <div key={f.id} className="border-b border-gray-100 last:border-0" data-testid={`vault-file-${f.id}`}>
              <div className="px-5 py-3 flex flex-wrap items-center gap-3 text-[13px]">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-[10.5px] uppercase font-bold text-[var(--rest-accent)]">{f.kind}</span>
                <span className="font-medium text-gray-800 truncate max-w-[220px]">{f.filename}</span>
                <span className="rounded-full bg-emerald-50 text-emerald-600 text-[10.5px] font-bold px-2 py-0.5">✓ {isEs ? 'Procesado' : 'Processed'}</span>
                <span className="text-gray-400 text-[11.5px]">📅 {f.created_at?.slice(0, 10)} · 👤 {f.uploaded_by_label || '—'} · {f.rows_imported} {isEs ? 'filas' : 'rows'} · v{f.version || 1}</span>
                {(f.versions || []).length > 0 && (
                  <button onClick={() => setVersionsOf(versionsOf === f.id ? null : f.id)} data-testid={`vault-versions-btn-${f.id}`}
                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-gray-500 hover:text-[var(--rest-accent)]">
                    <History className="w-3 h-3" /> {f.versions.length} {isEs ? 'versiones' : 'versions'}
                  </button>
                )}
                <span className="ml-auto flex items-center gap-1">
                  <span className="hidden sm:block text-[10.5px] text-gray-300 max-w-[200px] truncate" title={(f.used_by || []).join(', ')}>
                    🧠 {(f.used_by || []).slice(0, 2).join(', ')}
                  </span>
                  <Menu testid={`vault-menu-${f.id}`} items={[
                    [isEs ? 'Vista previa' : 'Preview', Eye, () => doPreview(f)],
                    [isEs ? 'Descargar' : 'Download', Download, () => download(f)],
                    ...(isDemo ? [] : [
                      [isEs ? 'Reemplazar' : 'Replace', RefreshCw, () => replace(f)],
                      [isEs ? 'Renombrar' : 'Rename', Pencil, () => rename(f)],
                      [isEs ? 'Eliminar' : 'Delete', Trash2, () => del(f, false), true],
                    ]),
                  ]} />
                </span>
              </div>
              {versionsOf === f.id && (f.versions || []).map((v) => (
                <div key={v.id} className="px-5 py-2 pl-12 bg-gray-50/60 border-t border-gray-100 flex flex-wrap items-center gap-3 text-[12px]" data-testid={`vault-version-${v.id}`}>
                  <History className="w-3 h-3 text-gray-300" />
                  <span className="text-gray-600">v{v.version || 1} · {v.filename}</span>
                  <span className="text-gray-400">{v.created_at?.slice(0, 10)} · {v.rows_imported} {isEs ? 'filas' : 'rows'}</span>
                  {!isDemo && (
                    <span className="ml-auto flex gap-3">
                      <button onClick={() => restore(v)} data-testid={`vault-restore-${v.id}`} className="text-[11.5px] font-semibold text-[var(--rest-accent)] hover:underline">{isEs ? 'Restaurar' : 'Restore'}</button>
                      <button onClick={() => download(v)} className="text-[11.5px] text-gray-500 hover:text-gray-900">{isEs ? 'Descargar' : 'Download'}</button>
                      <button onClick={() => del(v, true)} data-testid={`vault-delete-version-${v.id}`} className="text-[11.5px] text-gray-400 hover:text-red-500">{isEs ? 'Eliminar' : 'Delete'}</button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </RCard>
      ))}
      {!(d.categories || []).length && (
        <p className="mt-5 text-[13px] text-gray-400" data-testid="vault-empty">{isEs ? 'Tu Data Vault está vacío — sube tu primer reporte arriba.' : 'Your Data Vault is empty — upload your first report above.'}</p>
      )}
      {(d.categories || []).length > 0 && !categories.length && (
        <p className="mt-5 text-[13px] text-gray-400" data-testid="vault-no-results">{isEs ? 'Sin resultados para ese filtro.' : 'No results for that filter.'}</p>
      )}

      {(d.audit || []).length > 0 && (
        <RCard className="mt-5 p-5" testid="vault-audit">
          <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">{isEs ? 'Registro de actividad' : 'Audit log'}</p>
          {d.audit.slice(0, 8).map((a) => (
            <p key={a.id} className="text-[11.5px] text-gray-500 py-0.5">
              <span className="text-gray-400">{a.at?.slice(0, 16).replace('T', ' ')}</span> · <b className="text-gray-600">{a.action}</b> · {a.file} <span className="text-gray-400">· {a.by}</span>{a.detail ? <span className="text-gray-350"> — {a.detail}</span> : null}
            </p>
          ))}
        </RCard>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="vault-preview-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <p className="text-[14px] font-bold text-gray-900 mb-1">{preview.filename}</p>
            <p className="text-[11.5px] text-gray-400 mb-3">{isEs ? 'Primeras filas de' : 'First rows of'} {preview.total_rows}</p>
            <table className="w-full text-[12px]">
              <thead><tr className="text-left text-gray-400 uppercase text-[10px]">
                <th className="pb-1">Date</th><th className="pb-1 text-right">Total</th><th className="pb-1 text-right">Orders</th><th className="pb-1">Item</th><th className="pb-1">Channel</th>
              </tr></thead>
              <tbody>{preview.rows.map((r, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="py-1">{r.date}</td><td className="py-1 text-right tabular-nums">${r.total}</td>
                  <td className="py-1 text-right tabular-nums">{r.orders}</td><td className="py-1 truncate max-w-[160px]">{r.item}</td><td className="py-1">{r.channel}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
